import React, { memo, useCallback, useMemo, useState, useRef, useEffect, createContext, useContext } from 'react';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import { Box, Typography, IconButton, Avatar, CircularProgress } from '@mui/material';
import {
  MessageSquare, Image, ArrowRightCircle,
  User, Plus, X, Diamond, GitBranch,
  Film, Heart, Clock, Play, Square, Languages,
  UserPlus, SmilePlus, ChevronRight, Sparkles, Camera
} from 'lucide-react';
import ConfirmDeleteButton from '../components/ConfirmDeleteButton';
import SfEmojiPicker, { EmojiTriggerButton } from './SfEmojiPicker';
import VideoHoverPreview from '../components/VideoHoverPreview';

/** Context to provide characters list to dialogue nodes */
const SfCharactersContext = createContext([]);
export const SfCharactersProvider = SfCharactersContext.Provider;

const FLOW = '#6366f1';
const MUTED = 'rgba(161, 161, 170, 0.95)';

const ACCENT = {
  sfDialogue: 'rgb(167, 139, 250)',
  sfImage: 'rgb(248, 113, 113)',
  sfVideo: 'rgb(96, 165, 250)',
  sfNextChapter: 'rgb(251, 191, 36)',
  sfDecision: 'rgb(244, 114, 182)',
  sfBranch: 'rgb(56, 189, 248)',
  sfStart: 'rgb(74, 222, 128)',
  sfRpSwitch: 'rgb(251, 146, 60)',
  sfTimeskip: 'rgb(192, 132, 252)',
  sfIntroduce: 'rgb(52, 211, 153)',
  sfStatus: 'rgb(251, 191, 36)',
  sfInsta: 'rgb(217, 70, 239)'
};

/** Language context — 'ru' | 'en' */
const SfLanguageContext = createContext('ru');
export const SfLanguageProvider = SfLanguageContext.Provider;

/** Read-only context — hides editing controls when true */
const SfReadOnlyContext = createContext(false);
export const SfReadOnlyProvider = SfReadOnlyContext.Provider;

/** Actions context — provides translateNode/suggestDialogue from editor page */
const SfActionsContext = createContext(null);
export const SfActionsProvider = SfActionsContext.Provider;

const handleHitSx = {
  position: 'absolute',
  width: '100%',
  height: '100%',
  top: 0,
  left: 0,
  transform: 'none',
  border: 'none',
  background: 'transparent',
  opacity: 1
};

/* ── shared primitives ────────────────────────────────────────── */

function PinSingleDot({ color }) {
  return (
    <Box
      className="nodrag"
      sx={{
        position: 'absolute', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 6, height: 6, borderRadius: '50%',
        bgcolor: color, pointerEvents: 'none',
        boxShadow: `0 0 0 1px ${color}cc`
      }}
    />
  );
}

function SfNodeCard({ selected, ringColor, children, width = 280 }) {
  return (
    <Box
      className="wf-node-bleu wf-bleu-node-card"
      sx={{
        width,
        borderRadius: '8px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        bgcolor: '#18181b',
        boxShadow: selected
          ? `0 4px 6px -1px rgba(0,0,0,0.26), 0 2px 4px -2px rgba(0,0,0,0.2), ${ringColor} 0 0 0 2px`
          : '0 4px 6px -1px rgba(0,0,0,0.26), 0 2px 4px -2px rgba(0,0,0,0.2)',
        outline: 'none',
        overflow: 'visible'
      }}
    >
      {children}
    </Box>
  );
}

function SfHeader({ dotColor, title, nodeId, avatar, avatarFallback, extraAction, hideDelete }) {
  const readOnly = useContext(SfReadOnlyContext);
  const { deleteElements } = useReactFlow();
  const onRemove = useCallback(
    (e) => { e.stopPropagation(); e.preventDefault(); void deleteElements({ nodes: [{ id: nodeId }] }); },
    [nodeId, deleteElements]
  );

  return (
    <Box sx={{ p: 1, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1, mr: 2 }}>
          {avatar !== undefined ? (
            <Avatar
              src={avatar || undefined}
              sx={{ width: 18, height: 18, fontSize: '0.55rem', bgcolor: dotColor, flexShrink: 0 }}
            >
              {avatarFallback || <User size={10} />}
            </Avatar>
          ) : (
            <Box sx={{ width: 12, height: 12, borderRadius: '4px', bgcolor: dotColor, flexShrink: 0 }} />
          )}
          <Typography
            component="h3"
            sx={{ fontSize: '0.75rem', lineHeight: '14px', fontWeight: 600, color: '#fafafa', m: 0, cursor: 'default', userSelect: 'none' }}
            noWrap
          >
            {title}
          </Typography>
        </Box>
        {!readOnly && <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {extraAction}
          {!hideDelete && (
            <ConfirmDeleteButton
              onConfirm={onRemove}
              iconSize={13}
              className="nodrag nopan"
              sx={{ color: MUTED, '&:hover': { color: '#fca5a5', bgcolor: 'rgba(248, 113, 113, 0.12)' } }}
            />
          )}
        </Box>}
      </Box>
    </Box>
  );
}

function PinTarget({ id, style }) {
  return (
    <Box sx={{ position: 'absolute', top: 0, left: -4, width: 20, height: 20, zIndex: 2, ...style }}>
      <Handle type="target" position={Position.Left} id={id} className="wf-bleu-handle nodrag nopan" style={handleHitSx} />
      <PinSingleDot color={FLOW} />
    </Box>
  );
}

function PinSource({ id, style }) {
  return (
    <Box sx={{ position: 'absolute', top: 0, right: -4, width: 20, height: 20, ...style }}>
      <Handle type="source" position={Position.Right} id={id} className="wf-bleu-handle nodrag nopan" style={handleHitSx} />
      <PinSingleDot color={FLOW} />
    </Box>
  );
}

/* ── inline editable text ────────────────────────────────────── */

const sanitizeText = (s) => {
  if (s == null) return '';
  return String(s)
    .split('\n')
    .map((line) => line.replace(/ {2,}/g, ' ').trim())
    .filter((line) => line.length > 0)
    .join('\n');
};

const EditableTextInner = React.forwardRef(function EditableText({ value, displayValue, onChange, placeholder, sx: sxProp, multiline, onEnterAdd, renderDisplay, bubbleSx }, ref) {
  const readOnly = useContext(SfReadOnlyContext);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef(null);
  const emojiBtnRef = useRef(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const pickerOpenRef = useRef(false);
  const lastSelectionRef = useRef({ start: 0, end: 0 });

  // Stable refs for inline callbacks — updated on every render that memo allows
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onEnterAddRef = useRef(onEnterAdd);
  onEnterAddRef.current = onEnterAdd;
  const renderDisplayRef = useRef(renderDisplay);
  renderDisplayRef.current = renderDisplay;

  useEffect(() => { setDraft(value); }, [value]);

  const startEdit = useCallback((e) => {
    if (readOnly) return;
    if (e) e.stopPropagation();
    setEditing(true);
    setTimeout(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      const len = el.value?.length ?? 0;
      try { el.setSelectionRange(len, len); } catch (_) { /* ignore */ }
    }, 0);
  }, [readOnly]);

  React.useImperativeHandle(ref, () => ({ triggerEdit: () => startEdit(null) }), [startEdit]);

  const commit = useCallback(() => {
    if (pickerOpenRef.current) return;
    setEditing(false);
    setEmojiOpen(false);
    const flattened = multiline ? (draft || '') : (draft || '').replace(/[\r\n]+/g, ' ');
    const clean = sanitizeText(flattened);
    if (clean !== draft) setDraft(clean);
    if (clean !== value) onChangeRef.current(clean);
  }, [draft, value, multiline]);

  const handleEmojiSelect = useCallback((emoji) => {
    const cur = draft || '';
    const { start, end } = lastSelectionRef.current;
    const safeStart = Math.min(Math.max(0, start), cur.length);
    const safeEnd = Math.min(Math.max(safeStart, end), cur.length);
    const next = cur.slice(0, safeStart) + emoji + cur.slice(safeEnd);
    const newPos = safeStart + emoji.length;
    lastSelectionRef.current = { start: newPos, end: newPos };
    setDraft(next);
    onChangeRef.current(next);
    setEmojiOpen(false);
    pickerOpenRef.current = false;
    setTimeout(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      try { el.setSelectionRange(newPos, newPos); } catch (_) { /* ignore */ }
    }, 0);
  }, [draft]);

  const toggleEmoji = useCallback((e) => {
    e.stopPropagation();
    const el = inputRef.current;
    if (el) {
      lastSelectionRef.current = {
        start: el.selectionStart ?? (el.value?.length ?? 0),
        end: el.selectionEnd ?? (el.value?.length ?? 0)
      };
    }
    const next = !emojiOpen;
    setEmojiOpen(next);
    pickerOpenRef.current = next;
  }, [emojiOpen]);

  const handleEmojiClose = useCallback(() => {
    setEmojiOpen(false);
    pickerOpenRef.current = false;
    setTimeout(() => inputRef.current?.focus(), 0);
  }, []);

  const showValue = displayValue !== undefined ? displayValue : value;

  if (!editing) {
    if (renderDisplayRef.current) {
      return renderDisplayRef.current(showValue, startEdit);
    }
    return (
      <Typography
        className="nodrag"
        onDoubleClick={startEdit}
        sx={{ cursor: 'text', userSelect: 'none', ...sxProp }}
      >
        {showValue || placeholder}
      </Typography>
    );
  }

  const inputRow = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, maxWidth: '100%' }}>
      <EmojiTriggerButton buttonRef={emojiBtnRef} onClick={toggleEmoji} />
      <Box
        sx={{
          flex: '0 1 auto',
          minWidth: 0,
          maxWidth: '100%',
          display: 'grid',
          '&::after': {
            content: 'attr(data-replicated-value) " "',
            visibility: 'hidden',
            gridArea: '1 / 1 / 2 / 2',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontSize: 'inherit',
            fontFamily: 'inherit',
            lineHeight: 'inherit',
            fontWeight: 'inherit',
            minWidth: '1ch',
            ...sxProp
          },
          '& > textarea': {
            gridArea: '1 / 1 / 2 / 2',
            width: '100%',
            bgcolor: 'transparent',
            border: 'none',
            color: 'inherit',
            fontSize: 'inherit',
            fontFamily: 'inherit',
            lineHeight: 'inherit',
            fontWeight: 'inherit',
            p: 0,
            m: 0,
            outline: 'none',
            resize: 'none',
            overflow: 'hidden',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            display: 'block',
            ...sxProp
          }
        }}
        data-replicated-value={(draft || ' ') + ' '}
      >
        <Box
          component="textarea"
          rows={1}
          ref={inputRef}
          className="nodrag nopan"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onContextMenu={(e) => e.stopPropagation()}
          onMouseDown={(e) => { if (e.button === 2) e.stopPropagation(); }}
          onBlur={() => setTimeout(commit, 100)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') { setDraft(value); setEditing(false); setEmojiOpen(false); pickerOpenRef.current = false; return; }
            e.stopPropagation();
            if (e.key === 'Enter' && !multiline) {
              e.preventDefault();
              const clean = sanitizeText((draft || '').replace(/[\r\n]+/g, ' '));
              if (onEnterAddRef.current) { setEditing(false); if (clean !== value) onChangeRef.current(clean); onEnterAddRef.current(); }
              else { setEditing(false); if (clean !== value) onChangeRef.current(clean); }
            }
          }}
          autoFocus
        />
      </Box>
    </Box>
  );

  return (
    <>
      {bubbleSx ? <Box sx={bubbleSx}>{inputRow}</Box> : inputRow}
      <SfEmojiPicker
        anchorEl={emojiBtnRef.current}
        open={emojiOpen}
        onClose={handleEmojiClose}
        onSelect={handleEmojiSelect}
      />
    </>
  );
});

const editableTextEq = (a, b) =>
  a.value === b.value &&
  a.displayValue === b.displayValue &&
  a.placeholder === b.placeholder &&
  a.multiline === b.multiline &&
  a.bubbleSx === b.bubbleSx;

const EditableText = memo(EditableTextInner, editableTextEq);

/* ── multi-bubble speaker editor ─────────────────────────────── */

const SpeakerBubbleList = React.forwardRef(function SpeakerBubbleList({
  value, displayValue, isFallback, onChange, placeholder, bubbleSx, textColor, fontSize, fontWeight
}, ref) {
  const readOnly = useContext(SfReadOnlyContext);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [focusReq, setFocusReq] = useState(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [emojiForIdx, setEmojiForIdx] = useState(null);
  const pickerOpenRef = useRef(false);
  const inputRefs = useRef([]);
  const emojiBtnRefs = useRef([]);
  const lastSelectionRef = useRef({ idx: 0, start: 0, end: 0 });
  const valueRef = useRef(value);
  valueRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => { setDraft(value || ''); }, [value]);

  const startEdit = useCallback((e, lineIdx) => {
    if (readOnly) return;
    if (e) e.stopPropagation();
    setEditing(true);
    setFocusReq({ idx: lineIdx ?? 0, cursor: 'end', ts: Date.now() });
  }, [readOnly]);

  React.useImperativeHandle(ref, () => ({ triggerEdit: () => startEdit(null, 0) }), [startEdit]);

  const commit = useCallback(() => {
    if (pickerOpenRef.current) return;
    setEditing(false);
    setFocusReq(null);
    setEmojiOpen(false);
    const clean = sanitizeText(draft);
    if (clean !== draft) setDraft(clean);
    if (clean !== valueRef.current) onChangeRef.current(clean);
  }, [draft]);

  const cancel = useCallback(() => {
    setDraft(valueRef.current || '');
    setEditing(false);
    setFocusReq(null);
    setEmojiOpen(false);
    pickerOpenRef.current = false;
  }, []);

  const toggleEmoji = useCallback((i, e) => {
    e.stopPropagation();
    const el = inputRefs.current[i];
    if (el) {
      lastSelectionRef.current = {
        idx: i,
        start: el.selectionStart ?? (el.value?.length ?? 0),
        end: el.selectionEnd ?? (el.value?.length ?? 0)
      };
    } else {
      lastSelectionRef.current = { idx: i, start: 0, end: 0 };
    }
    setEmojiForIdx(i);
    const next = !emojiOpen;
    setEmojiOpen(next);
    pickerOpenRef.current = next;
  }, [emojiOpen]);

  const handleEmojiSelect = useCallback((emoji) => {
    const { idx, start, end } = lastSelectionRef.current;
    const rawLines = (draft || '').split('\n');
    const curLines = rawLines.length === 0 ? [''] : rawLines;
    const targetIdx = Math.min(Math.max(0, idx), curLines.length - 1);
    const line = curLines[targetIdx] || '';
    const safeStart = Math.min(Math.max(0, start), line.length);
    const safeEnd = Math.min(Math.max(safeStart, end), line.length);
    const newLine = line.slice(0, safeStart) + emoji + line.slice(safeEnd);
    const next = [...curLines]; next[targetIdx] = newLine;
    const newCursor = safeStart + emoji.length;
    lastSelectionRef.current = { idx: targetIdx, start: newCursor, end: newCursor };
    setDraft(next.join('\n'));
    setEmojiOpen(false);
    pickerOpenRef.current = false;
    setFocusReq({ idx: targetIdx, cursor: newCursor, ts: Date.now() });
  }, [draft]);

  const handleEmojiClose = useCallback(() => {
    setEmojiOpen(false);
    pickerOpenRef.current = false;
    const idx = emojiForIdx ?? 0;
    setTimeout(() => {
      const el = inputRefs.current[idx];
      if (el) el.focus();
    }, 0);
  }, [emojiForIdx]);

  useEffect(() => {
    if (!editing || !focusReq) return;
    const el = inputRefs.current[focusReq.idx];
    if (!el) return;
    el.focus();
    const pos = focusReq.cursor === 'start' ? 0
      : focusReq.cursor === 'end' ? el.value.length
      : (typeof focusReq.cursor === 'number' ? focusReq.cursor : el.value.length);
    try { el.setSelectionRange(pos, pos); } catch (_) { /* ignore */ }
  }, [editing, focusReq]);

  const showValue = displayValue !== undefined ? displayValue : value;

  if (!editing) {
    const messages = (showValue || '').split('\n').filter(Boolean);
    const msgSx = {
      fontSize, fontWeight,
      color: isFallback ? 'rgba(248, 113, 113, 0.9)' : textColor
    };
    if (messages.length === 0) {
      return (
        <Box sx={bubbleSx} className="nodrag" onDoubleClick={(e) => startEdit(e, 0)}>
          <Typography sx={{ fontSize, color: 'rgba(161, 161, 170, 0.5)', fontStyle: 'italic' }}>
            {placeholder}
          </Typography>
        </Box>
      );
    }
    return (
      <>
        {messages.map((msg, i) => (
          <Box key={i} sx={bubbleSx} className="nodrag" onDoubleClick={(e) => startEdit(e, i)}>
            <Typography sx={msgSx}>{msg}</Typography>
          </Box>
        ))}
      </>
    );
  }

  const rawLines = draft.split('\n');
  const lines = rawLines.length === 0 ? [''] : rawLines;
  inputRefs.current = inputRefs.current.slice(0, lines.length);

  const setLine = (i, v) => {
    const next = [...lines]; next[i] = v;
    setDraft(next.join('\n'));
  };
  const splitAt = (i, cursor) => {
    const cur = lines[i];
    const before = cur.slice(0, cursor);
    const after = cur.slice(cursor);
    const next = [...lines]; next[i] = before; next.splice(i + 1, 0, after);
    setDraft(next.join('\n'));
    setFocusReq({ idx: i + 1, cursor: 'start', ts: Date.now() });
  };
  const mergeUp = (i) => {
    if (i === 0) return;
    const prev = lines[i - 1];
    const cur = lines[i];
    const next = [...lines];
    next[i - 1] = prev + cur;
    next.splice(i, 1);
    setDraft(next.join('\n'));
    setFocusReq({ idx: i - 1, cursor: prev.length, ts: Date.now() });
  };

  const sharedTextSx = {
    fontSize, fontWeight, fontFamily: 'inherit', lineHeight: 'inherit',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word'
  };

  const { maxWidth: bubbleMaxWidth, alignSelf: bubbleAlignSelf, ...bubbleSxNoLayout } = bubbleSx;
  const bubbleGridSx = {
    ...bubbleSxNoLayout,
    maxWidth: '100%',
    display: 'grid',
    flex: '0 1 auto',
    minWidth: 0,
    '&::after': {
      content: 'attr(data-replicated-value) " "',
      visibility: 'hidden',
      gridArea: '1 / 1 / 2 / 2',
      ...sharedTextSx
    },
    '& > textarea': {
      gridArea: '1 / 1 / 2 / 2',
      width: '100%',
      bgcolor: 'transparent',
      border: 'none',
      outline: 'none',
      p: 0,
      m: 0,
      color: textColor,
      resize: 'none',
      overflow: 'hidden',
      ...sharedTextSx
    }
  };

  const rowSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 0.25,
    alignSelf: bubbleAlignSelf,
    maxWidth: typeof bubbleMaxWidth === 'string'
      ? `calc(${bubbleMaxWidth} + 20px)`
      : bubbleMaxWidth
  };

  return (
    <>
      {lines.map((line, i) => (
        <Box key={i} sx={rowSx}>
          <Box sx={bubbleGridSx} data-replicated-value={(line || ' ') + ' '}>
            <Box
              component="textarea"
              rows={1}
              ref={(el) => { inputRefs.current[i] = el; }}
              className="nodrag nopan"
              value={line}
              placeholder={i === 0 && lines.length === 1 && !line ? placeholder : ''}
              onChange={(e) => setLine(i, e.target.value)}
              onPaste={(e) => {
                const text = e.clipboardData?.getData('text') || '';
                if (!text.includes('\n') && !text.includes('\r')) return;
                e.preventDefault();
                const pasteLines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
                const el = e.target;
                const selStart = el.selectionStart ?? 0;
                const selEnd = el.selectionEnd ?? 0;
                const cur = lines[i] || '';
                const before = cur.slice(0, selStart);
                const after = cur.slice(selEnd);
                const next = [...lines];
                next[i] = before + pasteLines[0];
                const tail = pasteLines.slice(1);
                tail[tail.length - 1] = (tail[tail.length - 1] || '') + after;
                next.splice(i + 1, 0, ...tail);
                setDraft(next.join('\n'));
                const newIdx = i + pasteLines.length - 1;
                const newCursor = pasteLines[pasteLines.length - 1].length;
                setFocusReq({ idx: newIdx, cursor: newCursor, ts: Date.now() });
              }}
              onContextMenu={(e) => e.stopPropagation()}
              onMouseDown={(e) => { if (e.button === 2) e.stopPropagation(); }}
              onBlur={() => setTimeout(() => {
                if (pickerOpenRef.current) return;
                const active = document.activeElement;
                if (!inputRefs.current.includes(active)) commit();
              }, 50)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') { cancel(); return; }
                e.stopPropagation();
                if (e.key === 'Enter') {
                  e.preventDefault();
                  splitAt(i, e.target.selectionStart ?? line.length);
                  return;
                }
                if (e.key === 'Backspace' && e.target.selectionStart === 0 && e.target.selectionEnd === 0) {
                  e.preventDefault();
                  mergeUp(i);
                  return;
                }
                if (e.key === 'ArrowUp' && i > 0) {
                  e.preventDefault();
                  setFocusReq({ idx: i - 1, cursor: 'end', ts: Date.now() });
                  return;
                }
                if (e.key === 'ArrowDown' && i < lines.length - 1) {
                  e.preventDefault();
                  setFocusReq({ idx: i + 1, cursor: 'end', ts: Date.now() });
                  return;
                }
              }}
            />
          </Box>
          <EmojiTriggerButton
            buttonRef={(el) => { emojiBtnRefs.current[i] = el; }}
            onClick={(e) => toggleEmoji(i, e)}
          />
        </Box>
      ))}
      <SfEmojiPicker
        anchorEl={emojiForIdx != null ? emojiBtnRefs.current[emojiForIdx] : null}
        open={emojiOpen}
        onClose={handleEmojiClose}
        onSelect={handleEmojiSelect}
      />
    </>
  );
});

/* ── chat bubble styles ──────────────────────────────────────── */

const speakerBubbleSx = {
  bgcolor: 'rgba(167, 139, 250, 0.12)',
  borderRadius: '8px 8px 8px 2px',
  px: 1, py: 0.5,
  maxWidth: '80%',
  alignSelf: 'flex-start'
};

const videoBubbleSx = {
  bgcolor: 'rgba(96, 165, 250, 0.12)',
  borderRadius: '8px 8px 8px 2px',
  px: 1, py: 0.5,
  maxWidth: '80%',
  alignSelf: 'flex-start'
};

const answerBubbleSx = {
  bgcolor: 'rgba(99, 102, 241, 0.18)',
  borderRadius: '8px 8px 2px 8px',
  px: 1, py: 0.5,
  maxWidth: '85%',
  '&:has(textarea)': {
    maxWidth: 'calc(85% + 20px)'
  },
  alignSelf: 'flex-end'
};

/* ── Fake waveform bars for audio bubble ─────────────────────── */

const WAVEFORM_BARS = [4, 8, 6, 11, 5, 13, 4, 10, 7, 12, 5, 9, 6, 11, 4, 8, 12, 5, 10, 6, 9, 13, 5, 8, 11, 4, 9, 7, 6, 10];

function AudioWaveformBubble({ audioUrl, bubbleSx, accentColor }) {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef(null);
  const rafRef = useRef(null);

  const stopTracking = useCallback(() => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
  }, []);

  const trackProgress = useCallback(() => {
    const a = audioRef.current;
    if (a && a.duration) setProgress(a.currentTime / a.duration);
    rafRef.current = requestAnimationFrame(trackProgress);
  }, []);

  const toggle = useCallback((e) => {
    e.stopPropagation();
    if (playing) {
      stopTracking();
      if (audioRef.current) { audioRef.current.pause(); audioRef.current.src = ''; audioRef.current = null; }
      setPlaying(false);
      setProgress(0);
      return;
    }
    if (audioUrl) {
      const a = new Audio(audioUrl);
      a.addEventListener('ended', () => { stopTracking(); setPlaying(false); setProgress(0); audioRef.current = null; });
      a.play().catch(() => {});
      audioRef.current = a;
      setPlaying(true);
      rafRef.current = requestAnimationFrame(trackProgress);
    }
  }, [playing, audioUrl, stopTracking, trackProgress]);

  useEffect(() => () => {
    stopTracking();
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
  }, [stopTracking]);

  const barCount = WAVEFORM_BARS.length;

  return (
    <Box sx={{ ...bubbleSx, display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Box
        className="nodrag"
        onClick={toggle}
        sx={{ cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16 }}
      >
        {playing
          ? <Square size={12} fill={accentColor} style={{ color: accentColor }} />
          : <Play size={12} fill={accentColor} style={{ color: accentColor }} />}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '1.5px', height: 14, flex: 1 }}>
        {WAVEFORM_BARS.map((h, i) => {
          const played = i / barCount < progress;
          return (
            <Box key={i} sx={{ width: 2, minWidth: 2, height: h, borderRadius: 1, bgcolor: accentColor, opacity: played ? 0.9 : 0.3, transition: 'opacity 0.1s' }} />
          );
        })}
      </Box>
    </Box>
  );
}

/* ── Relationship badge ──────────────────────────────────────── */

function RelBadge({ value }) {
  if (value == null || value === 0) return null;
  const color = value > 0 ? 'rgb(74, 222, 128)' : 'rgb(248, 113, 113)';
  const sign = value > 0 ? '+' : '';
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', px: 0.5,
      borderRadius: '4px',
      bgcolor: value > 0 ? 'rgba(74, 222, 128, 0.12)' : 'rgba(248, 113, 113, 0.12)',
      ml: 0.5
    }}>
      <Typography sx={{ fontSize: '0.55rem', fontWeight: 600, color, lineHeight: 1.4 }}>
        {sign}{value}
      </Typography>
    </Box>
  );
}

/* ════════════════════════════════════════════════════════════════
   DIALOGUE NODE — messenger-style
   ════════════════════════════════════════════════════════════════ */

function SfDialogueNode({ id, data, selected }) {
  const { setNodes } = useReactFlow();
  const readOnly = useContext(SfReadOnlyContext);
  const characters = useContext(SfCharactersContext);
  const lang = useContext(SfLanguageContext);
  const character = data.speakerId ? characters.find((c) => c.id === data.speakerId) : null;
  const speaker = character?.name || data.speakerName || 'Speaker';
  const speakerAvatar = character?.avatar?.url64 || data.speakerAvatar || null;
  const activeText = lang === 'en' ? data.text_en : data.text;
  const otherText = lang === 'en' ? data.text : data.text_en;
  const isFallbackText = !activeText && !!otherText;
  const text = activeText || otherText || '';
  const activeVariants = (lang === 'en' ? data.variants_en : data.variants) || [];
  const otherVariants = (lang === 'en' ? data.variants : data.variants_en) || [];
  const relationships = useMemo(() => data.relationships || [], [data.relationships]);
  const tiers = useMemo(() => data.tiers || [], [data.tiers]);
  const answerCount = Math.max(activeVariants.length, otherVariants.length);
  const hasAnswers = answerCount > 0;
  const lastAnswerRef = useRef(null);
  const prevAnswerCount = useRef(answerCount);
  const prevLang = useRef(lang);
  const [translating, setTranslating] = useState(false);
  const sfActions = useContext(SfActionsContext);

  const isEmpty = !data.text && (!data.variants || data.variants.filter(Boolean).length === 0);

  const handleTranslate = useCallback(async (e) => {
    e.stopPropagation();
    if (translating || !sfActions) return;
    setTranslating(true);
    try {
      await sfActions.translateNode(id);
    } finally {
      setTranslating(false);
    }
  }, [translating, sfActions, id]);

  const handleSuggest = useCallback(async (e) => {
    e.stopPropagation();
    if (translating || !sfActions) return;
    setTranslating(true);
    try {
      await sfActions.suggestDialogue(id);
    } finally {
      setTranslating(false);
    }
  }, [translating, sfActions, id]);

  const patch = useCallback((p) => {
    setNodes((nds) => nds.map((n) => n.id === id ? { ...n, data: { ...n.data, ...p } } : n));
  }, [id, setNodes]);

  const addAnswer = useCallback(() => {
    const varKey = lang === 'en' ? 'variants_en' : 'variants';
    const otherKey = lang === 'en' ? 'variants' : 'variants_en';
    setNodes((nds) => nds.map((n) => {
      if (n.id !== id) return n;
      const curVars = (lang === 'en' ? n.data.variants_en : n.data.variants) || [];
      const otherVars = (lang === 'en' ? n.data.variants : n.data.variants_en) || [];
      const curR = n.data.relationships || [];
      const curT = n.data.tiers || [];
      return { ...n, data: { ...n.data, [varKey]: [...curVars, ''], [otherKey]: [...otherVars, ''], relationships: [...curR, 0], tiers: [...curT, ''] } };
    }));
  }, [id, lang, setNodes]);

  const removeAnswer = useCallback((idx) => {
    const varKey = lang === 'en' ? 'variants_en' : 'variants';
    const curVars = (lang === 'en' ? data.variants_en : data.variants) || [];
    const newV = curVars.filter((_, i) => i !== idx);
    const newR = relationships.filter((_, i) => i !== idx);
    const newT = tiers.filter((_, i) => i !== idx);
    const otherKey = lang === 'en' ? 'variants' : 'variants_en';
    const otherVars = (lang === 'en' ? data.variants : data.variants_en) || [];
    const newOther = otherVars.filter((_, i) => i !== idx);
    patch({ [varKey]: newV, [otherKey]: newOther, relationships: newR, tiers: newT });
  }, [lang, data, relationships, tiers, patch]);

  // Auto-focus last answer when a new one is added (not on language switch)
  useEffect(() => {
    const langChanged = prevLang.current !== lang;
    prevLang.current = lang;
    if (langChanged) { prevAnswerCount.current = answerCount; return; }
    if (answerCount > prevAnswerCount.current && lastAnswerRef.current) {
      lastAnswerRef.current.triggerEdit?.();
    }
    prevAnswerCount.current = answerCount;
  }, [answerCount, lang]);

  return (
    <SfNodeCard selected={selected} ringColor={ACCENT.sfDialogue} width={320}>
      <SfHeader
        dotColor={ACCENT.sfDialogue}
        title={speaker}
        nodeId={id}
        avatar={speakerAvatar}
        avatarFallback={speaker[0]}
        extraAction={
          <>
            <Box
              className="nodrag nopan"
              onClick={(e) => { e.stopPropagation(); addAnswer(); }}
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.3,
                cursor: 'pointer', px: 0.75, py: 0.25,
                borderRadius: '4px', bgcolor: 'rgba(99, 102, 241, 0.14)',
                '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.24)' }
              }}
            >
              <Plus size={10} style={{ color: 'rgb(129, 140, 248)' }} />
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: 'rgb(129, 140, 248)' }}>Answer</Typography>
            </Box>
            {isEmpty ? (
              <IconButton
                size="small"
                className="nodrag nopan"
                onClick={handleSuggest}
                disabled={translating}
                sx={{ p: 0.4, color: MUTED, '&:hover': { color: '#a78bfa', bgcolor: 'rgba(139,92,246,0.12)' } }}
              >
                {translating ? <CircularProgress size={13} sx={{ color: MUTED }} /> : <Sparkles size={13} />}
              </IconButton>
            ) : (
              <IconButton
                size="small"
                className="nodrag nopan"
                onClick={handleTranslate}
                disabled={translating}
                sx={{ p: 0.4, color: MUTED, '&:hover': { color: '#a78bfa', bgcolor: 'rgba(139,92,246,0.12)' } }}
              >
                {translating ? <CircularProgress size={13} sx={{ color: MUTED }} /> : <Languages size={13} />}
              </IconButton>
            )}
          </>
        }
      />
      <Box sx={{ position: 'relative', minHeight: 36 }}>
        {/* Fixed-position input pin */}
        <PinTarget id="flow-in" style={{ top: 10, left: 4 }} />

        {/* Speaker messages — left side */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, p: 1, pl: 3, pr: 3 }}>
          {data.audio && (
            <AudioWaveformBubble audioUrl={data.audioUrl} bubbleSx={speakerBubbleSx} accentColor="rgba(167, 139, 250, 0.9)" />
          )}
          <SpeakerBubbleList
            value={activeText || ''}
            displayValue={text}
            isFallback={isFallbackText}
            onChange={(v) => patch({ [lang === 'en' ? 'text_en' : 'text']: v })}
            placeholder="Double-click to edit..."
            bubbleSx={speakerBubbleSx}
            textColor="rgba(210, 195, 255, 0.95)"
            fontSize="0.7rem"
            fontWeight={500}
          />
        </Box>

        {/* Separator */}
        {hasAnswers && (
          <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.06)', mx: 1 }} />
        )}

        {/* Answers — right side, chat bubbles */}
        {hasAnswers && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, p: 1, pl: 3, pr: 3, alignItems: 'flex-end' }}>
            {Array.from({ length: answerCount }, (_, i) => {
              const v = activeVariants[i] || '';
              const tier = tiers[i];
              const isVariantFallback = !v && !!otherVariants[i];
              const displayVariantText = v || otherVariants[i] || '';
              return (
                <Box key={i} sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 0.5, maxWidth: '100%', justifyContent: 'flex-end', width: '100%' }}>
                  {!readOnly && (
                    <IconButton
                      size="small"
                      className="nodrag nopan"
                      onClick={(e) => { e.stopPropagation(); removeAnswer(i); }}
                      sx={{ p: 0.2, color: 'rgba(248,113,113,0.6)', '&:hover': { color: '#fca5a5' }, flexShrink: 0, visibility: selected ? 'visible' : 'hidden' }}
                    >
                      <X size={10} />
                    </IconButton>
                  )}
                  <RelBadge value={relationships[i]} />
                  <Box sx={answerBubbleSx}>
                    <EditableText
                      ref={i === answerCount - 1 ? lastAnswerRef : undefined}
                      value={v}
                      displayValue={displayVariantText}
                      onChange={(val) => {
                        const varKey = lang === 'en' ? 'variants_en' : 'variants';
                        setNodes((nds) => nds.map((n) => {
                          if (n.id !== id) return n;
                          const cur = (lang === 'en' ? n.data.variants_en : n.data.variants) || [];
                          const nv = [...cur]; while (nv.length <= i) nv.push(''); nv[i] = val;
                          return { ...n, data: { ...n.data, [varKey]: nv } };
                        }));
                      }}
                      placeholder={`Answer ${i + 1}`}
                      onEnterAdd={addAnswer}
                      sx={{ fontSize: '0.7rem', fontWeight: 500, color: isVariantFallback ? 'rgba(248, 113, 113, 0.9)' : 'rgba(225, 230, 255, 0.95)' }}
                    />
                    {tier && (
                      <Typography sx={{ fontSize: '0.55rem', color: 'rgb(96, 165, 250)', fontWeight: 600 }}>
                        ✦ {tier}
                      </Typography>
                    )}
                  </Box>
                  <PinSource id={`choice-${i}`} style={{ top: 4, right: -20 }} />
                </Box>
              );
            })}
          </Box>
        )}

        {/* No answers — single flow-out pin at bottom */}
        {!hasAnswers && (
          <Box sx={{ position: 'relative', minHeight: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', pb: 0.5 }}>
            <PinSource id="flow-out" style={{ top: 2, right: 4 }} />
          </Box>
        )}

      </Box>
    </SfNodeCard>
  );
}

/* ════════════════════════════════════════════════════════════════
   IMAGE NODE
   ════════════════════════════════════════════════════════════════ */

function SfImageNode({ id, data, selected }) {
  const previewSrc = data.imagePreview512 || null;
  return (
    <SfNodeCard selected={selected} ringColor={ACCENT.sfImage} width={220}>
      <SfHeader
        dotColor={ACCENT.sfImage}
        title={data.label || 'Image'}
        nodeId={id}
        extraAction={data.playerSends ? (
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', px: 0.75, py: 0.15,
            borderRadius: '4px', bgcolor: 'rgba(99, 102, 241, 0.12)'
          }}>
            <Typography sx={{ fontSize: '0.55rem', fontWeight: 600, color: '#a78bfa', lineHeight: 1.4 }}>
              Player Sends
            </Typography>
          </Box>
        ) : null}
      />
      {previewSrc && (
        <Box sx={{ p: 0.75, pb: 0 }}>
          <Box
            component="img"
            src={previewSrc}
            sx={{
              display: 'block',
              width: '100%',
              height: 'auto',
              borderRadius: '4px',
              objectFit: 'contain'
            }}
          />
        </Box>
      )}
      <Box sx={{ p: 1, position: 'relative' }}>
        <Box sx={{ position: 'relative', pl: 2, pr: 2, minHeight: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <PinTarget id="flow-in" style={{ top: 2 }} />
          {!previewSrc && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: 2 }}>
              <Typography className="nodrag" sx={{ fontSize: '0.75rem', fontWeight: 500, color: MUTED }}>
                {data.imageName || 'Select image...'}
              </Typography>
            </Box>
          )}
          <PinSource id="flow-out" style={{ top: 2 }} />
        </Box>
      </Box>
    </SfNodeCard>
  );
}

/* ════════════════════════════════════════════════════════════════
   NEXT CHAPTER NODE
   ════════════════════════════════════════════════════════════════ */

function SfNextChapterNode({ id, data, selected }) {
  return (
    <SfNodeCard selected={selected} ringColor={ACCENT.sfNextChapter} width={220}>
      <SfHeader dotColor={ACCENT.sfNextChapter} title={data.label || 'Next Chapter'} nodeId={id} />
      <Box sx={{ p: 1, position: 'relative' }}>
        <Box sx={{ position: 'relative', pl: 2, pr: 2, minHeight: 24, display: 'flex', alignItems: 'center' }}>
          <PinTarget id="flow-in" style={{ top: 2 }} />
          <Typography className="nodrag" sx={{ fontSize: '0.75rem', fontWeight: 500, color: MUTED, pl: 2 }}>
            {data.targetChapterName || 'Select chapter...'}
          </Typography>
        </Box>
      </Box>
    </SfNodeCard>
  );
}

/* ════════════════════════════════════════════════════════════════
   DECISION NODE — important player choice, dynamic enum options
   ════════════════════════════════════════════════════════════════ */

function SfDecisionNode({ id, data, selected }) {
  const options = data.options || (data.optionA ? [data.optionA, data.optionB || 'Option B'] : ['Option A', 'Option B']);

  return (
    <SfNodeCard selected={selected} ringColor={ACCENT.sfDecision} width={240}>
      <SfHeader dotColor={ACCENT.sfDecision} title={data.label || 'Decision'} nodeId={id} />
      <Box sx={{ px: 1, pt: 0.5, pb: 1 }}>
        <Box sx={{ px: 1.5, py: 1, mb: 0.5 }}>
          <Typography className="nodrag" sx={{ fontSize: '0.7rem', color: MUTED, fontWeight: 600, textAlign: 'center' }}>
            {data.decisionName || 'Decision name...'}
          </Typography>
        </Box>
        <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.08)', mx: 0.5, mb: 0.5 }} />
        {options.map((opt, i) => (
          <Box key={i} sx={{ position: 'relative', display: 'flex', alignItems: 'center', minHeight: 28, pl: 2, pr: 2, mb: i < options.length - 1 ? 0.5 : 0 }}>
            <PinTarget id={`decision-in-${i}`} style={{ top: 4 }} />
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 500, color: MUTED }}>
                {opt}
              </Typography>
            </Box>
            <PinSource id={`decision-out-${i}`} style={{ top: 4 }} />
          </Box>
        ))}
      </Box>
    </SfNodeCard>
  );
}

/* ════════════════════════════════════════════════════════════════
   SWITCH NODE — branch on a past decision (dynamic outputs)
   ════════════════════════════════════════════════════════════════ */

function SfBranchNode({ id, data, selected }) {
  const decisionRef = data.decisionRef || '';
  const options = data.switchOptions || (data.branchOptionA ? [data.branchOptionA, data.branchOptionB || 'B'] : []);

  return (
    <SfNodeCard selected={selected} ringColor={ACCENT.sfBranch} width={220}>
      <SfHeader dotColor={ACCENT.sfBranch} title={'Switch'} nodeId={id} />
      <Box sx={{ p: 1 }}>
        <Box sx={{ position: 'relative', pl: 2, pr: 2, minHeight: 24, display: 'flex', alignItems: 'center', mb: 0.5 }}>
          <PinTarget id="flow-in" style={{ top: 2 }} />
          <Typography className="nodrag" sx={{ fontSize: '0.75rem', fontWeight: 500, color: MUTED, pl: 2 }}>
            {decisionRef || 'Select decision...'}
          </Typography>
        </Box>
        {options.length > 0 && (
          <>
            <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.08)', mx: 0.5, mb: 0.5 }} />
            {options.map((opt, i) => (
              <Box key={i} sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', minHeight: 24, pr: 2, mb: i < options.length - 1 ? 0.5 : 0 }}>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 500, color: MUTED, mr: 0.5 }}>
                  {opt}
                </Typography>
                <PinSource id={`switch-out-${i}`} style={{ top: 2 }} />
              </Box>
            ))}
          </>
        )}
      </Box>
    </SfNodeCard>
  );
}

/* ════════════════════════════════════════════════════════════════
   START NODE — entry point with previous chapter summary
   ════════════════════════════════════════════════════════════════ */

function SfStartNode({ id, data, selected }) {
  const readOnly = useContext(SfReadOnlyContext);
  const lang = useContext(SfLanguageContext);
  const summary = lang === 'en' ? (data.previousSummary_en || data.previousSummary || '') : (data.previousSummary || '');

  return (
    <SfNodeCard selected={selected} ringColor={ACCENT.sfStart} width={280}>
      <SfHeader dotColor={ACCENT.sfStart} title={'Start'} nodeId={id} hideDelete />
      <Box sx={{ p: 1 }}>
        {/* Summary from previous chapter */}
        {summary ? (
          <Box sx={{ mb: 1, p: 0.75, borderRadius: '6px', bgcolor: 'rgba(250,250,250,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: 'rgba(250,250,250,0.5)', mb: 0.25, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Previously
            </Typography>
            <Typography sx={{ fontSize: '0.68rem', color: 'rgba(250,250,250,0.7)', lineHeight: 1.4 }}>
              {summary}
            </Typography>
          </Box>
        ) : (
          <Typography sx={{ fontSize: '0.68rem', color: 'rgba(161,161,170,0.5)', fontStyle: 'italic', mb: 0.5, px: 0.5 }}>
            {readOnly ? 'Chapter start' : (selected ? 'Select to load AI summary of previous chapter' : 'Chapter start')}
          </Typography>
        )}

        {/* Past decisions count */}
        {(data.pastDecisionCount > 0) && (
          <Box sx={{ mb: 0.5, px: 0.5 }}>
            <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: 'rgba(250,250,250,0.5)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Past Decisions: {data.pastDecisionCount}
            </Typography>
          </Box>
        )}

        {/* Flow out */}
        <Box sx={{ position: 'relative', minHeight: 24, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', pr: 2 }}>
          <PinSource id="flow-out" style={{ top: 2 }} />
        </Box>
      </Box>
    </SfNodeCard>
  );
}

/* ════════════════════════════════════════════════════════════════
   VIDEO NODE — like Dialogue but with video selector, no character
   ════════════════════════════════════════════════════════════════ */

function SfVideoNode({ id, data, selected }) {
  const { setNodes } = useReactFlow();
  const readOnly = useContext(SfReadOnlyContext);
  const lang = useContext(SfLanguageContext);
  const activeText = lang === 'en' ? data.text_en : data.text;
  const otherText = lang === 'en' ? data.text : data.text_en;
  const isFallbackText = !activeText && !!otherText;
  const text = activeText || otherText || '';
  const activeVariants = (lang === 'en' ? data.variants_en : data.variants) || [];
  const otherVariants = (lang === 'en' ? data.variants : data.variants_en) || [];
  const relationships = useMemo(() => data.relationships || [], [data.relationships]);
  const tiers = useMemo(() => data.tiers || [], [data.tiers]);
  const answerCount = Math.max(activeVariants.length, otherVariants.length);
  const hasAnswers = answerCount > 0;
  const lastAnswerRef = useRef(null);
  const prevAnswerCount = useRef(answerCount);
  const prevLang = useRef(lang);
  const [translating, setTranslating] = useState(false);
  const sfActions = useContext(SfActionsContext);

  const handleTranslate = useCallback(async (e) => {
    e.stopPropagation();
    if (translating || !sfActions) return;
    setTranslating(true);
    try {
      await sfActions.translateNode(id);
    } finally {
      setTranslating(false);
    }
  }, [translating, sfActions, id]);

  const patch = useCallback((p) => {
    setNodes((nds) => nds.map((n) => n.id === id ? { ...n, data: { ...n.data, ...p } } : n));
  }, [id, setNodes]);

  const addAnswer = useCallback(() => {
    const varKey = lang === 'en' ? 'variants_en' : 'variants';
    const otherKey = lang === 'en' ? 'variants' : 'variants_en';
    setNodes((nds) => nds.map((n) => {
      if (n.id !== id) return n;
      const curVars = (lang === 'en' ? n.data.variants_en : n.data.variants) || [];
      const otherVars = (lang === 'en' ? n.data.variants : n.data.variants_en) || [];
      const curR = n.data.relationships || [];
      const curT = n.data.tiers || [];
      return { ...n, data: { ...n.data, [varKey]: [...curVars, ''], [otherKey]: [...otherVars, ''], relationships: [...curR, 0], tiers: [...curT, ''] } };
    }));
  }, [id, lang, setNodes]);

  const removeAnswer = useCallback((idx) => {
    const varKey = lang === 'en' ? 'variants_en' : 'variants';
    const curVars = (lang === 'en' ? data.variants_en : data.variants) || [];
    const newV = curVars.filter((_, i) => i !== idx);
    const newR = relationships.filter((_, i) => i !== idx);
    const newT = tiers.filter((_, i) => i !== idx);
    const otherKey = lang === 'en' ? 'variants' : 'variants_en';
    const otherVars = (lang === 'en' ? data.variants : data.variants_en) || [];
    const newOther = otherVars.filter((_, i) => i !== idx);
    patch({ [varKey]: newV, [otherKey]: newOther, relationships: newR, tiers: newT });
  }, [lang, data, relationships, tiers, patch]);

  useEffect(() => {
    const langChanged = prevLang.current !== lang;
    prevLang.current = lang;
    if (langChanged) { prevAnswerCount.current = answerCount; return; }
    if (answerCount > prevAnswerCount.current && lastAnswerRef.current) {
      lastAnswerRef.current.triggerEdit?.();
    }
    prevAnswerCount.current = answerCount;
  }, [answerCount, lang]);

  return (
    <SfNodeCard selected={selected} ringColor={ACCENT.sfVideo} width={320}>
      <SfHeader
        dotColor={ACCENT.sfVideo}
        title={data.label || 'Video'}
        nodeId={id}
        extraAction={
          <>
            <Box
              className="nodrag nopan"
              onClick={(e) => { e.stopPropagation(); addAnswer(); }}
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.3,
                cursor: 'pointer', px: 0.75, py: 0.25,
                borderRadius: '4px', bgcolor: 'rgba(99, 102, 241, 0.14)',
                '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.24)' }
              }}
            >
              <Plus size={10} style={{ color: 'rgb(129, 140, 248)' }} />
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: 'rgb(129, 140, 248)' }}>Answer</Typography>
            </Box>
            <IconButton
              size="small"
              className="nodrag nopan"
              onClick={handleTranslate}
              disabled={translating}
              sx={{ p: 0.4, color: MUTED, '&:hover': { color: '#60a5fa', bgcolor: 'rgba(96,165,250,0.12)' } }}
            >
              {translating ? <CircularProgress size={13} sx={{ color: MUTED }} /> : <Languages size={13} />}
            </IconButton>
          </>
        }
      />
      <Box sx={{ position: 'relative', minHeight: 36 }}>
        <PinTarget id="flow-in" style={{ top: 10, left: 4 }} />

        {/* Video preview or placeholder */}
        {data.videoPreview512 ? (
          <Box className="nodrag" sx={{ p: 0.75, pb: 0 }}>
            <VideoHoverPreview
              thumbnailSrc={data.videoPreview512}
              videoSrc={data.videoUrl}
              sx={{
                width: '100%',
                aspectRatio: '1',
                borderRadius: '4px',
              }}
            />
          </Box>
        ) : (
          <Box sx={{ pl: 2.95, pr: 2, pt: 1.4, pb: 1, position: 'relative' }}>
            <Typography className="nodrag" sx={{ fontSize: '0.75rem', fontWeight: 500, color: MUTED, pl: 2 }}>
              {data.videoName || 'Select video...'}
            </Typography>
          </Box>
        )}

        <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.06)', mx: 1 }} />

        {/* Text messages — left side */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, p: 1, pl: 3, pr: 3 }}>
          {data.audio && (
            <AudioWaveformBubble audioUrl={data.audioUrl} bubbleSx={videoBubbleSx} accentColor="rgba(96, 165, 250, 0.9)" />
          )}
          <SpeakerBubbleList
            value={activeText || ''}
            displayValue={text}
            isFallback={isFallbackText}
            onChange={(v) => patch({ [lang === 'en' ? 'text_en' : 'text']: v })}
            placeholder="Double-click to edit..."
            bubbleSx={videoBubbleSx}
            textColor="rgba(170, 210, 255, 0.95)"
            fontSize="0.7rem"
            fontWeight={500}
          />
        </Box>

        {/* Separator */}
        {hasAnswers && (
          <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.06)', mx: 1 }} />
        )}

        {/* Answers — right side */}
        {hasAnswers && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, p: 1, pl: 3, pr: 3, alignItems: 'flex-end' }}>
            {Array.from({ length: answerCount }, (_, i) => {
              const v = activeVariants[i] || '';
              const tier = tiers[i];
              const isVariantFallback = !v && !!otherVariants[i];
              const displayVariantText = v || otherVariants[i] || '';
              return (
                <Box key={i} sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 0.5, maxWidth: '100%', justifyContent: 'flex-end', width: '100%' }}>
                  {!readOnly && (
                    <IconButton
                      size="small"
                      className="nodrag nopan"
                      onClick={(e) => { e.stopPropagation(); removeAnswer(i); }}
                      sx={{ p: 0.2, color: 'rgba(248,113,113,0.6)', '&:hover': { color: '#fca5a5' }, flexShrink: 0, visibility: selected ? 'visible' : 'hidden' }}
                    >
                      <X size={10} />
                    </IconButton>
                  )}
                  <RelBadge value={relationships[i]} />
                  <Box sx={answerBubbleSx}>
                    <EditableText
                      ref={i === answerCount - 1 ? lastAnswerRef : undefined}
                      value={v}
                      displayValue={displayVariantText}
                      onChange={(val) => {
                        const varKey = lang === 'en' ? 'variants_en' : 'variants';
                        setNodes((nds) => nds.map((n) => {
                          if (n.id !== id) return n;
                          const cur = (lang === 'en' ? n.data.variants_en : n.data.variants) || [];
                          const nv = [...cur]; while (nv.length <= i) nv.push(''); nv[i] = val;
                          return { ...n, data: { ...n.data, [varKey]: nv } };
                        }));
                      }}
                      placeholder={`Answer ${i + 1}`}
                      onEnterAdd={addAnswer}
                      sx={{ fontSize: '0.7rem', fontWeight: 500, color: isVariantFallback ? 'rgba(248, 113, 113, 0.9)' : 'rgba(225, 230, 255, 0.95)' }}
                    />
                    {tier && (
                      <Typography sx={{ fontSize: '0.55rem', color: 'rgb(96, 165, 250)', fontWeight: 600 }}>
                        ✦ {tier}
                      </Typography>
                    )}
                  </Box>
                  <PinSource id={`choice-${i}`} style={{ top: 4, right: -20 }} />
                </Box>
              );
            })}
          </Box>
        )}

        {/* No answers — single flow-out */}
        {!hasAnswers && (
          <Box sx={{ position: 'relative', minHeight: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', pb: 0.5 }}>
            <PinSource id="flow-out" style={{ top: 2, right: 4 }} />
          </Box>
        )}
      </Box>
    </SfNodeCard>
  );
}

/* ════════════════════════════════════════════════════════════════
   RP SWITCH NODE — character relationship check
   ════════════════════════════════════════════════════════════════ */

function SfRpSwitchNode({ id, data, selected }) {
  const characters = useContext(SfCharactersContext);
  const character = data.characterId ? characters.find((c) => c.id === data.characterId) : null;
  const charName = character?.name || data.characterName || 'Character';
  const level = data.relationshipLevel ?? 0;

  return (
    <SfNodeCard selected={selected} ringColor={ACCENT.sfRpSwitch} width={240}>
      <SfHeader
        dotColor={ACCENT.sfRpSwitch}
        title="RP Switch"
        nodeId={id}
        avatar={character?.avatar?.url64 || null}
        avatarFallback={charName[0]}
      />
      <Box sx={{ px: 1, pt: 0.5, pb: 1 }}>
        <Box sx={{ position: 'relative', pl: 2, mb: 0.75, display: 'flex', alignItems: 'center', minHeight: 28 }}>
          <PinTarget id="flow-in" style={{ top: 4, transform: 'none' }} />
          <Typography className="nodrag" sx={{ fontSize: '0.7rem', color: MUTED, fontWeight: 600, ml: 2 }}>
            {charName} ≥ {level} RP
          </Typography>
        </Box>
        {/* >= output */}
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', minHeight: 24, pr: 2, mb: 0.5 }}>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 500, color: MUTED, mr: 0.5 }}>
            Enough
          </Typography>
          <PinSource id="rp-out-pass" style={{ top: 2 }} />
        </Box>
        {/* < output */}
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', minHeight: 24, pr: 2 }}>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 500, color: MUTED, mr: 0.5 }}>
            Not Enough
          </Typography>
          <PinSource id="rp-out-fail" style={{ top: 2 }} />
        </Box>
      </Box>
    </SfNodeCard>
  );
}

/* ════════════════════════════════════════════════════════════════
   TIMESKIP NODE — set time or skip forward
   ════════════════════════════════════════════════════════════════ */

function SfTimeskipNode({ id, data, selected }) {
  const mode = data.timeskipMode || 'set'; // 'set' | 'skip'
  const hours = data.hours ?? 0;
  const minutes = data.minutes ?? 0;

  return (
    <SfNodeCard selected={selected} ringColor={ACCENT.sfTimeskip} width={220}>
      <SfHeader dotColor={ACCENT.sfTimeskip} title="Timeskip" nodeId={id} />
      <Box sx={{ p: 1, position: 'relative' }}>
        <Box sx={{ position: 'relative', pl: 2, pr: 2, minHeight: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <PinTarget id="flow-in" style={{ top: 2 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: 2 }}>
            <Clock size={12} style={{ color: ACCENT.sfTimeskip, flexShrink: 0 }} />
            <Typography className="nodrag" sx={{ fontSize: '0.7rem', fontWeight: 500, color: 'rgba(192, 132, 252, 0.85)' }}>
              {mode === 'set'
                ? `Set ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
                : `${hours > 0 ? `${hours}h ` : ''}${minutes}m later`}
            </Typography>
          </Box>
          <PinSource id="flow-out" style={{ top: 2 }} />
        </Box>
      </Box>
    </SfNodeCard>
  );
}

/* ════════════════════════════════════════════════════════════════
   INTRODUCE NODE — unlock a character for the player
   ════════════════════════════════════════════════════════════════ */

function SfIntroduceNode({ id, data, selected }) {
  const characters = useContext(SfCharactersContext);
  const character = data.characterId ? characters.find((c) => c.id === data.characterId) : null;
  const charName = character?.name || data.characterName || 'Character';
  const charAvatar = character?.avatar?.url64 || null;

  return (
    <SfNodeCard selected={selected} ringColor={ACCENT.sfIntroduce} width={240}>
      <SfHeader dotColor={ACCENT.sfIntroduce} title="Introduce" nodeId={id} />
      <Box sx={{ p: 1, position: 'relative' }}>
        <Box sx={{ position: 'relative', pl: 2, pr: 2, minHeight: 40, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <PinTarget id="flow-in" style={{ top: 10 }} />
          {character ? (
            <Box sx={{
              flex: 1, display: 'flex', alignItems: 'center', gap: 1, ml: 2, mr: 1,
              p: 0.75, borderRadius: '8px',
              bgcolor: 'rgba(52, 211, 153, 0.08)',
              border: '1px solid rgba(52, 211, 153, 0.2)'
            }}>
              <Avatar src={charAvatar || undefined} sx={{ width: 28, height: 28, fontSize: '0.65rem', bgcolor: ACCENT.sfIntroduce }}>
                {charName[0]}
              </Avatar>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: 'rgba(52, 211, 153, 0.9)', flex: 1 }} noWrap>
                {charName}
              </Typography>
              <ChevronRight size={14} style={{ color: 'rgba(52, 211, 153, 0.5)', flexShrink: 0 }} />
            </Box>
          ) : (
            <Typography className="nodrag" sx={{ fontSize: '0.7rem', fontWeight: 500, color: MUTED, ml: 2 }}>
              Select character...
            </Typography>
          )}
          <PinSource id="flow-out" style={{ top: 10 }} />
        </Box>
      </Box>
    </SfNodeCard>
  );
}

/* ════════════════════════════════════════════════════════════════
   STATUS NODE — set emoji status for a character
   ════════════════════════════════════════════════════════════════ */

function SfStatusNode({ id, data, selected }) {
  const characters = useContext(SfCharactersContext);
  const character = data.characterId ? characters.find((c) => c.id === data.characterId) : null;
  const charName = character?.name || data.characterName || 'Character';
  const charAvatar = character?.avatar?.url64 || null;
  const emoji = data.statusEmoji || '';

  return (
    <SfNodeCard selected={selected} ringColor={ACCENT.sfStatus} width={240}>
      <SfHeader dotColor={ACCENT.sfStatus} title="Status" nodeId={id} />
      <Box sx={{ p: 1, position: 'relative' }}>
        <Box sx={{ position: 'relative', pl: 2, pr: 2, minHeight: 40, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <PinTarget id="flow-in" style={{ top: 10 }} />
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1, ml: 2, mr: 1 }}>
            {character ? (
              <Avatar src={charAvatar || undefined} sx={{ width: 22, height: 22, fontSize: '0.55rem', bgcolor: ACCENT.sfStatus }}>
                {charName[0]}
              </Avatar>
            ) : null}
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 500, color: character ? '#fafafa' : MUTED }} noWrap>
              {character ? charName : 'Select character...'}
            </Typography>
            {emoji && (
              <Box sx={{ fontSize: '1.1rem', lineHeight: 1, flexShrink: 0 }}>{emoji}</Box>
            )}
          </Box>
          <PinSource id="flow-out" style={{ top: 10 }} />
        </Box>
      </Box>
    </SfNodeCard>
  );
}

/* ════════════════════════════════════════════════════════════════
   INSTA POST NODE — in-game fake Instagram post
   ════════════════════════════════════════════════════════════════ */

function SfInstaNode({ id, data, selected }) {
  const characters = useContext(SfCharactersContext);
  const character = data.characterId ? characters.find((c) => c.id === data.characterId) : null;
  const charName = character?.name || data.characterName || '';
  const charAvatar = character?.avatar?.url64 || null;
  const previewSrc = data.imagePreview512 || null;

  return (
    <SfNodeCard selected={selected} ringColor={ACCENT.sfInsta} width={220}>
      <SfHeader dotColor={ACCENT.sfInsta} title="Insta Post" nodeId={id} />
      {previewSrc && (
        <Box sx={{ p: 0.75, pb: 0 }}>
          <Box component="img" src={previewSrc} sx={{ display: 'block', width: '100%', aspectRatio: '1', borderRadius: '4px', objectFit: 'cover' }} />
        </Box>
      )}
      <Box sx={{ p: 1, position: 'relative' }}>
        <Box sx={{ position: 'relative', pl: 2, pr: 2, minHeight: 32, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <PinTarget id="flow-in" style={{ top: 4 }} />
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.75, ml: 2, mr: 1 }}>
            {character ? (
              <Avatar src={charAvatar || undefined} sx={{ width: 20, height: 20, fontSize: '0.5rem', bgcolor: ACCENT.sfInsta }}>{charName[0]}</Avatar>
            ) : null}
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 500, color: charName ? '#fafafa' : MUTED }} noWrap>
              {charName || 'Select character...'}
            </Typography>
            {data.instaSection && (
              <Typography sx={{ fontSize: '0.55rem', fontWeight: 600, color: 'rgba(217,70,239,0.7)', textTransform: 'uppercase' }}>
                {data.instaSection}
              </Typography>
            )}
          </Box>
          {data.likes > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mr: 1 }}>
              <Heart size={10} style={{ color: '#f87171', fill: '#f87171' }} />
              <Typography sx={{ fontSize: '0.55rem', color: 'rgba(250,250,250,0.5)' }}>{data.likes}</Typography>
            </Box>
          )}
          <PinSource id="flow-out" style={{ top: 4 }} />
        </Box>
      </Box>
    </SfNodeCard>
  );
}

/* ════════════════════════════════════════════════════════════════
   EXPORTS
   ════════════════════════════════════════════════════════════════ */

export const sfNodeTypes = {
  sfDialogue: memo(SfDialogueNode),
  sfImage: memo(SfImageNode),
  sfVideo: memo(SfVideoNode),
  sfNextChapter: memo(SfNextChapterNode),
  sfDecision: memo(SfDecisionNode),
  sfBranch: memo(SfBranchNode),
  sfStart: memo(SfStartNode),
  sfRpSwitch: memo(SfRpSwitchNode),
  sfTimeskip: memo(SfTimeskipNode),
  sfIntroduce: memo(SfIntroduceNode),
  sfStatus: memo(SfStatusNode),
  sfInsta: memo(SfInstaNode),
  // backward compat
  sfNextFlow: memo(SfNextChapterNode)
};

/** Palette metadata for Storyflow context menus */
export const SF_PALETTE_ITEMS = [
  {
    type: 'sfImage',
    title: 'Image',
    Icon: Image,
    iconColor: '#f87171',
    iconWellBg: 'rgba(248, 113, 113, 0.14)',
    defaultData: { label: 'Image' }
  },
  {
    type: 'sfDecision',
    title: 'Decision',
    Icon: Diamond,
    iconColor: '#f472b6',
    iconWellBg: 'rgba(244, 114, 182, 0.14)',
    defaultData: { label: 'Decision', decisionName: '', options: ['Option A', 'Option B'] }
  },
  {
    type: 'sfBranch',
    title: 'Switch',
    Icon: GitBranch,
    iconColor: '#38bdf8',
    iconWellBg: 'rgba(56, 189, 248, 0.14)',
    defaultData: { label: 'Switch', decisionRef: '', switchOptions: [] }
  },
  {
    type: 'sfVideo',
    title: 'Video',
    Icon: Film,
    iconColor: '#60a5fa',
    iconWellBg: 'rgba(96, 165, 250, 0.14)',
    defaultData: { label: 'Video', videoName: '', videoUrl: '', variants: [], variants_en: [], relationships: [], tiers: [] }
  },
  {
    type: 'sfRpSwitch',
    title: 'RP Switch',
    Icon: Heart,
    iconColor: '#fb923c',
    iconWellBg: 'rgba(251, 146, 60, 0.14)',
    defaultData: { label: 'RP Switch', characterId: '', characterName: '', relationshipLevel: 0 }
  },
  {
    type: 'sfTimeskip',
    title: 'Timeskip',
    Icon: Clock,
    iconColor: '#c084fc',
    iconWellBg: 'rgba(192, 132, 252, 0.14)',
    defaultData: { label: 'Timeskip', timeskipMode: 'set', hours: 0, minutes: 0 }
  },
  {
    type: 'sfIntroduce',
    title: 'Introduce',
    Icon: UserPlus,
    iconColor: '#34d399',
    iconWellBg: 'rgba(52, 211, 153, 0.14)',
    defaultData: { label: 'Introduce', characterId: '', characterName: '' }
  },
  {
    type: 'sfStatus',
    title: 'Status',
    Icon: SmilePlus,
    iconColor: '#fbbf24',
    iconWellBg: 'rgba(251, 191, 36, 0.14)',
    defaultData: { label: 'Status', characterId: '', characterName: '', statusEmoji: '' }
  },
  {
    type: 'sfInsta',
    title: 'Insta Post',
    Icon: Camera,
    iconColor: '#d946ef',
    iconWellBg: 'rgba(217, 70, 239, 0.14)',
    defaultData: { label: 'Insta Post', characterId: '', characterName: '', imageName: '', imageUrl: '', imagePreview512: '', instaSection: 'public', caption: '', likes: 0 }
  },
  {
    type: 'sfNextChapter',
    title: 'Next Chapter',
    Icon: ArrowRightCircle,
    iconColor: '#fbbf24',
    iconWellBg: 'rgba(251, 191, 36, 0.14)',
    defaultData: { label: 'Next Chapter' }
  }
];

/** Characters appear as Dialogue nodes in palette */
export function buildCharacterPaletteItems(characters) {
  return (characters || []).map((c) => ({
    type: 'sfDialogue',
    title: c.name,
    Icon: MessageSquare,
    iconColor: '#a78bfa',
    iconWellBg: 'rgba(167, 139, 250, 0.14)',
    avatarUrl: c.avatar?.url64 || null,
    defaultData: {
      label: c.name,
      speakerId: c.id,
      text: '',
      text_en: '',
      variants: [],
      variants_en: [],
      relationships: [],
      tiers: []
    }
  }));
}
